import { useCallback, useEffect, useRef, useState } from 'react';
import './IdleLogout.css';

const IDLE_TIMEOUT_MS = 60 * 60 * 1000; // 1 hour
const WARNING_BEFORE_MS = 60 * 1000; // show warning 60s before logout
const ACTIVITY_THROTTLE_MS = 5000;
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];

export function IdleLogout({ onLogout }) {
  const [secondsLeft, setSecondsLeft] = useState(null);
  const warnTimer = useRef(null);
  const logoutTimer = useRef(null);
  const countdownTimer = useRef(null);
  const lastResetRef = useRef(0);

  const clearTimers = useCallback(() => {
    clearTimeout(warnTimer.current);
    clearTimeout(logoutTimer.current);
    clearInterval(countdownTimer.current);
  }, []);

  const scheduleTimers = useCallback(() => {
    clearTimers();
    setSecondsLeft(null);

    warnTimer.current = setTimeout(() => {
      let remaining = Math.round(WARNING_BEFORE_MS / 1000);
      setSecondsLeft(remaining);
      countdownTimer.current = setInterval(() => {
        remaining -= 1;
        setSecondsLeft(remaining);
        if (remaining <= 0) clearInterval(countdownTimer.current);
      }, 1000);
    }, IDLE_TIMEOUT_MS - WARNING_BEFORE_MS);

    logoutTimer.current = setTimeout(onLogout, IDLE_TIMEOUT_MS);
  }, [clearTimers, onLogout]);

  const handleActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastResetRef.current < ACTIVITY_THROTTLE_MS) return;
    lastResetRef.current = now;
    scheduleTimers();
  }, [scheduleTimers]);

  useEffect(() => {
    scheduleTimers();
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, handleActivity));
    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, handleActivity));
      clearTimers();
    };
  }, [handleActivity, scheduleTimers, clearTimers]);

  if (secondsLeft === null) return null;

  return (
    <div className="idle-warning-overlay">
      <div className="idle-warning-modal">
        <h2>Still there?</h2>
        <p>You'll be signed out in {Math.max(secondsLeft, 0)}s due to inactivity.</p>
        <button type="button" onClick={handleActivity}>Stay signed in</button>
      </div>
    </div>
  );
}
